<template>
  <div class="promotion-management-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>حساب النتائج السنوية وإدارة الترفيع</h2>
        </div>
      </template>

      <!-- اختيار السنة الدراسية -->
      <el-form label-position="top">
        <el-form-item label="اختر السنة الدراسية لعرض الصفوف">
          <el-select v-model="selectedStudyYearId" placeholder="اختر السنة الدراسية" @change="fetchClasses" clearable style="width: 100%;">
            <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
          </el-select>
        </el-form-item>
      </el-form>
      
      <el-divider v-if="selectedStudyYearId" />

      <!-- جدول عرض حالة الصفوف -->
      <div v-if="selectedStudyYearId">
        <el-table :data="classes" v-loading="loading" border style="width: 100%">
          <el-table-column prop="name" label="اسم الصف" />
          <el-table-column prop="grade_level.name" label="المستوى الدراسي" />
          <el-table-column prop="students_count" label="عدد الطلاب" width="120" />
          
          <el-table-column label="حالة النتائج" width="150">
            <template #default="{ row }">
              <el-tag v-if="row.is_finalized" type="success">تم الاعتماد</el-tag>
              <el-tag v-else type="warning">قيد الانتظار</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="الإجراءات" width="550">
            <template #default="{ row }">
              <el-button
                :type="row.is_finalized ? 'warning' : 'primary'"
                :loading="row.is_calculating"
                :disabled="row.students_count === 0"
                @click="runFinalGradeCalculation(row)"
              >
                <el-icon><Refresh /></el-icon> {{ row.is_finalized ? 'إعادة حساب النتائج' : 'حساب النتائج النهائية' }}
              </el-button>
              
              <el-button
                type="info"
                :disabled="!row.is_finalized"
                @click="openPrintDialog(row, 'collective')"
              >
                <el-icon><Document /></el-icon> طباعة السجل الشامل
              </el-button>
              
              <el-button
                type="success"
                :disabled="!row.is_finalized"
                @click="openPrintDialog(row, 'individual')"
              >
                <el-icon><Printer /></el-icon> طباعة الشهادات الفردية
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else>
        <el-empty description="الرجاء اختيار السنة الدراسية لعرض الصفوف." />
      </div>
    </el-card>

    <!-- === Dialog موحد لاختيار القالب === -->
    <el-dialog v-model="printDialogVisible" :title="dialogTitle" width="40%">
      <div v-if="selectedClassForPrinting">
        <p>
          أنت على وشك طباعة تقارير لـ <strong>{{ selectedClassForPrinting.students_count }}</strong> طالب في صف
          <strong>{{ selectedClassForPrinting.name }}</strong>.
        </p>
        <el-form-item label="الرجاء اختيار القالب المطلوب">
          <el-select v-model="selectedTemplateId" placeholder="اختر قالب" style="width: 100%;">
            <el-option v-for="template in templates" :key="template.id" :label="template.name" :value="template.id" />
          </el-select>
        </el-form-item>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="printDialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="handlePrintRequest" :loading="isPrinting">
            <el-icon><View /></el-icon> عرض وطباعة
          </el-button>
        </span>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElNotification } from 'element-plus';
import { Refresh, Document, Printer, View } from '@element-plus/icons-vue';

export default {
  name: 'PromotionManagement',
  components: { Refresh, Document, Printer, View },
  data() {
    return {
      api: null,
      loading: false,
      studyYears: [],
      selectedStudyYearId: null,
      classes: [],
      printDialogVisible: false,
      isPrinting: false,
      selectedClassForPrinting: null,
      templates: [],
      selectedTemplateId: null,
      printType: null,
    };
  },
  computed: {
    dialogTitle() {
      if (this.printType === 'individual') {
        return 'طباعة الشهادات النهائية الفردية';
      }
      if (this.printType === 'collective') {
        return 'طباعة السجل النهائي الشامل';
      }
      return 'طباعة التقارير';
    }
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchStudyYears();
  },
  methods: {
    async fetchStudyYears() {
        try {
          const response = await this.api.get('/study-years');
          this.studyYears = response.data.data || response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الأعوام الدراسية.');
        }
    },
    async fetchClasses() {
      if (!this.selectedStudyYearId) {
        this.classes = [];
        return;
      }
      this.loading = true;
      try {
        const response = await this.api.get('/final-grade-calculation/classes', { params: { study_year_id: this.selectedStudyYearId } });
        this.classes = response.data.map(cls => ({ ...cls, is_calculating: false }));
      } catch (error) {
        ElMessage.error('فشل في جلب الصفوف.');
      } finally {
        this.loading = false;
      }
    },
    async runFinalGradeCalculation(classRow) {
      classRow.is_calculating = true;
      try {
        const response = await this.api.post('/final-grade-calculation/run', { class_id: classRow.id });
        ElNotification({
          title: 'Success',
          message: response.data.message || 'تم حساب النتائج النهائية بنجاح.',
          type: 'success',
        });
        this.fetchClasses();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'حدث خطأ أثناء حساب النتائج.');
      } finally {
        classRow.is_calculating = false;
      }
    },
    async openPrintDialog(classRow, type) {
      this.selectedClassForPrinting = classRow;
      this.printType = type;
      await this.fetchReportCardTemplates();
      this.printDialogVisible = true;
    },
    async fetchReportCardTemplates() {
      try {
        const response = await this.api.get('/report-card-templates');
        this.templates = response.data;
        if (this.templates.length > 0) {
          this.selectedTemplateId = this.templates[0].id;
        }
      } catch (error) {
        ElMessage.error('فشل في جلب قوالب الشهادات.');
      }
    },
    handlePrintRequest() {
      if (!this.selectedTemplateId) {
        ElMessage.warning('الرجاء اختيار قالب للطباعة.');
        return;
      }

      let routeName;
      if (this.printType === 'collective') {
        routeName = 'FinalRecordViewer'; 
      } else if (this.printType === 'individual') {
        // --- التعديل الرئيسي هنا: استخدام اسم المسار الصحيح ---
        routeName = 'StudentFinalTranscriptViewer'; 
      } else {
        return;
      }
      
      const routeData = this.$router.resolve({
        name: routeName,
        query: { 
          class_id: this.selectedClassForPrinting.id,
          template_id: this.selectedTemplateId
        }
      });

      window.open(routeData.href, '_blank');
      this.printDialogVisible = false;
    },
  },
};
</script>

<style scoped>
.promotion-management-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
</style>
